import { html, customElement, TemplateResult } from '@adobe/edex/ui/edex-lit';
import { styleMap } from '@adobe/edex/ui/edex-lit';
import { classMap } from '@adobe/edex/ui/edex-lit';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-alert';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-edit';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-delete';

import { $t } from '@adobe/edex/ui/shared/config/i18n';
import { I18nKey } from '@adobe/edex/ui/shared/constants/i18n';
import { formatDateString } from '@adobe/edex/ui/utils';
import { getAvatarURL } from '@adobe/edex/ui/shared/utils';
import { MarshalledComment, EntityType } from '@adobe/edex/ui/types';
import { EdexTagName, EdexDomID } from '@adobe/edex/ui/shared/constants';

import './comment-form';
import { BaseAuthElement } from '@adobe/edex/ui/components/base.element';
import { CommentFormState } from '@adobe/edex/ui/store/reducers/forms/comment';
import { selectCommentForm } from '@adobe/edex/ui/store/selectors';
import { setCommentForm } from '@adobe/edex/ui/store/routines';
import { getMemberUrl } from '@adobe/edex/ui/shared/utils/routes';

interface ToolbarItem {
  render: () => TemplateResult;
  show: boolean;
}

import(
  /* webpackChunkName: "component/comment-styles" */
  /* webpackMode: "lazy" */
  '@adobe/edex/ui/components/shared/comments/comment.scss'
);

@customElement(EdexTagName.comment)
export class Comment extends BaseAuthElement {
  static get properties() {
    return {
      ...super.properties,
      comment: { type: Object },
      form: { attribute: false },
      collapsed: { attribute: false },
      collapsible: { attribute: false },
    };
  }
  comment: MarshalledComment;
  form: CommentFormState;
  collapsed = true;
  collapsible = false;
  highlight = false;

  get useLightDom(): boolean {
    return true;
  }

  stateChanged(state) {
    super.stateChanged(state);
    this.form = selectCommentForm(state);
  }

  firstUpdated() {
    setTimeout(() => {
      if (this.querySelector('.comment-body edex-html')?.clientHeight > 50) {
        this.collapsible = true;
      }
    });
  }

  actions = {
    setCommentForm: setCommentForm.trigger,
  };

  get isOwnComment() {
    return this.isOwner(this.comment);
  }

  get spacing() {
    return (this.comment.nestingLevel - 1) * 4;
  }

  get canEdit(): boolean {
    const { isOwnComment, isAdmin } = this;
    return isOwnComment || isAdmin;
  }

  get canReport(): boolean {
    const { isGuest, isOwnComment } = this;
    return Boolean(!isGuest && !isOwnComment);
  }

  get showEditForm() {
    return this.form.data?.id === this.comment.id;
  }

  get showReplyForm() {
    return this.form.data?.parentCommentID === this.comment.id && !this.form.data?.id;
  }

  handleEdit() {
    const { comment } = this;
    this.actions.setCommentForm(comment);
  }

  handleReply() {
    const {
      comment: { entityID, id },
    } = this;
    this.actions.setCommentForm({
      entityID,
      parentCommentID: id,
      entityType: EntityType.Resource,
    });
  }

  get toolbarItems(): ToolbarItem[] {
    const { canEdit, canReport, comment } = this;
    return [
      {
        render: () => html`
          <sp-link id=${EdexDomID.editCommentBtn} quiet @click="${this.handleEdit}">
            <span>
              <sp-icon slot="icon" size="s">
                <sp-icon-edit></sp-icon-edit>
              </sp-icon>
              ${$t(I18nKey.edit)}
            </span>
          </sp-link>
        `,
        show: canEdit,
      },
      {
        render: () => html`
          <sp-link id=${EdexDomID.deleteCommentBtn} quiet @click="${this.handleDelete}">
            <span>
              <sp-icon slot="icon" size="s">
                <sp-icon-delete></sp-icon-delete>
              </sp-icon>
              ${$t(I18nKey.delete)}
            </span>
          </sp-link>
        `,
        show: canEdit,
      },
      {
        render: () => html`
          <sp-link quiet id=${EdexDomID.reportCommentBtn} @click="${this.handleReport}">
            <span>
              <sp-icon
                slot="icon"
                size="s"
                style=${styleMap({
                  color: comment.flagged ? 'red' : 'inherit',
                })}
              >
                <sp-icon-alert></sp-icon-alert>
              </sp-icon>
              ${$t(I18nKey.report)}
            </span>
          </sp-link>
        `,
        show: canReport,
      },
    ].filter(({ show }) => show);
  }

  renderToolbar() {
    return html`
      <div class="my-comment-actions">${this.toolbarItems.map(({ render }) => render())}</div>
    `;
  }

  renderHeader = () => {
    const {
      comment: {
        author: { fullName, vanityURL, avatarURL },
        createdAt,
      },
    } = this;
    return html`
      <div class="comment-header">
        <sp-link href="${getMemberUrl(vanityURL)}" rel="external" target="_top">
          <sp-avatar label="${fullName}" src="${getAvatarURL(avatarURL)}"></sp-avatar>
        </sp-link>
        <div class="header-info">
          <sp-link quiet href="${getMemberUrl(vanityURL)}" rel="external" target="_top" class="author-name">
            ${fullName}
          </sp-link>
          <span class="date">${formatDateString(createdAt)}</span>
        </div>
      </div>
    `;
  };

  handleReport() {
    this.emitEvent('report', this.comment);
  }

  handleDelete() {
    this.emitEvent('delete', this.comment);
  }

  renderFooter() {
    const { isGuest, showEditForm } = this;
    if (isGuest || showEditForm) {
      return '';
    }
    return html`
      <div class="comment-footer">
        ${this.renderToolbar()}
        <sp-link id=${EdexDomID.replyCommentBtn} quiet @click=${this.handleReply}>${$t(I18nKey.reply)}</sp-link>
      </div>
    `;
  }

  renderBody() {
    const { collapsible, collapsed } = this;
    if (this.showEditForm) {
      return '';
    }
    return html`
      <div
        class="comment-body ${classMap({
          collapsible,
          collapsed: collapsible && collapsed,
          highlight: this.highlight,
        })}"
        @click=${() => (this.collapsed = !this.collapsed)}
      >
        <edex-html .source=${this.comment.comment}></edex-html>
      </div>
    `;
  }

  renderEditForm() {
    if (!this.showEditForm) {
      return '';
    }
    return html`
      <edex-comment-form id=${EdexDomID.editCommentForm}></edex-comment-form>
    `;
  }

  renderReplyForm() {
    if (!this.showReplyForm) {
      return '';
    }
    return html`
      <edex-comment-form id=${EdexDomID.replyCommentForm}></edex-comment-form>
    `;
  }

  render() {
    const { comment } = this;
    return html`
      <div
        style="${styleMap({ 'margin-left': `${this.spacing}rem` })}"
        class="edex-comment ${classMap({ isNested: comment.nestingLevel > 1 })}"
      >
        ${this.renderHeader()} ${this.renderEditForm()} ${this.renderBody()} ${this.renderFooter()}
        ${this.renderReplyForm()}
      </div>
    `;
  }
}
